import React, { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";

const links = [
  { label: "Inicio", href: "#inicio" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Servicios", href: "#servicios" },
  { label: "Equipos", href: "#equipos" },
  { label: "Testimonios", href: "#testimonios" },
  { label: "Contacto", href: "#contacto" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Block body scroll when menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close on ESC key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handleClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    // Small delay so the menu closes before scrolling
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 50);
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "glass-nav shadow-jr-soft" : "bg-transparent"
        }`}
      >
        <nav className="container-jr flex items-center justify-between h-[64px] md:h-[72px]">
          <a
            href="#inicio"
            onClick={(e) => handleClick(e, "#inicio")}
            className="flex items-center"
            data-testid="navbar-logo-link"
          >
            <Logo
              variant="navbar"
              textColor={scrolled ? "oxford" : "white"}
              size={scrolled ? 36 : 40}
              className="h-8 md:h-11"
            />
          </a>

          {/* Desktop links */}
          <ul className="hidden lg:flex items-center gap-9">
            {links.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={(e) => handleClick(e, link.href)}
                  data-testid={`nav-link-${link.href.replace("#", "")}`}
                  className={`text-[14px] font-medium tracking-wide transition-colors duration-200 ${
                    scrolled
                      ? "text-jr-oxford hover:text-jr-cyan"
                      : "text-white/90 hover:text-jr-cyan"
                  }`}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="hidden lg:flex items-center">
            <a
              href="#contacto"
              onClick={(e) => handleClick(e, "#contacto")}
              data-testid="navbar-cta-button"
              className="px-5 py-3 rounded-xl bg-jr-yale text-white text-[13px] font-semibold tracking-wide uppercase hover:bg-jr-cyan transition-colors duration-200 shadow-jr-soft"
            >
              Contáctanos
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2.5 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            data-testid="navbar-mobile-toggle"
          >
            {open ? (
              <X className={scrolled ? "text-jr-oxford" : "text-white"} size={24} />
            ) : (
              <Menu className={scrolled ? "text-jr-oxford" : "text-white"} size={24} />
            )}
          </button>
        </nav>
      </header>

      {/* Mobile menu — full screen overlay */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop — click outside to close */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[55] bg-black/40"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />

            {/* Slide-in panel from right */}
            <motion.div
              key="menu"
              ref={menuRef}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.28, ease: "easeInOut" }}
              className="fixed top-0 right-0 bottom-0 z-[60] w-[80vw] max-w-[320px] bg-white shadow-2xl flex flex-col"
              data-testid="navbar-mobile-menu"
            >
              {/* Header row */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-jr-gray-bg">
                <Logo variant="navbar" textColor="oxford" size={36} />
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Cerrar menú"
                  className="p-2 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center text-jr-oxford hover:text-jr-cyan transition-colors"
                >
                  <X size={22} />
                </button>
              </div>

              {/* Links */}
              <ul className="flex flex-col px-4 py-4 flex-1">
                {links.map((link, i) => (
                  <motion.li
                    key={link.href}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 + 0.1 }}
                  >
                    <a
                      href={link.href}
                      onClick={(e) => handleClick(e, link.href)}
                      data-testid={`nav-mobile-link-${link.href.replace("#", "")}`}
                      className="flex items-center py-4 px-3 text-jr-oxford text-[16px] font-medium hover:text-jr-cyan border-b border-jr-gray-bg/60 transition-colors min-h-[52px]"
                    >
                      {link.label}
                    </a>
                  </motion.li>
                ))}

                <motion.li
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: links.length * 0.05 + 0.15 }}
                  className="mt-6 px-3"
                >
                  <a
                    href="#contacto"
                    onClick={(e) => handleClick(e, "#contacto")}
                    data-testid="navbar-mobile-cta-button"
                    className="block text-center px-5 py-4 rounded-xl bg-jr-yale text-white text-[14px] font-semibold uppercase tracking-wide hover:bg-jr-cyan transition-colors min-h-[52px] flex items-center justify-center"
                  >
                    Contáctanos
                  </a>
                </motion.li>
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
